<template>
  <div class="page-container">
    <h1>Fleet Growth</h1>

    <filter-panel
      class="filters-panel"
      :filter-parameters="computedFilterParameters"
      @update="applyFilter"
      :api-request-data="apiRequestData" />

    <b-row>
      <b-col lg="12" class="d-flex">
        <periodic-stats
          class="tradeflow-card"
          title="Fleet development"
          :seasonal="true"
          :grouped="true"
          :group-choices="groupChoices"
          :range-choices="rangeChoices"
          :stacked="true"
          :data-labels="true"
          :stats="periodicStat.development.data"
          @search="(parameters) => {
            applyFilter({
              _periodicStatSection: 'development',
              _periodicStatRange: parameters.range,
              _periodicStatGroup: parameters.group,
            })
          }"
          :apiRequestIsPerforming="!!getActiveRequestsCount"
          :export-types="exportTypes"
          @export="format => handleExport(periodicStat.development, format)"
        />
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="12" class="d-flex">
        <periodic-stats
          class="tradeflow-card"
          title="Total fleet size (vessels on water)"
          :seasonal="false"
          :grouped="true"
          :group-choices="groupChoices"
          :range-choices="rangeChoices"
          graphDefaultType="line"
          :stacked="true"
          :data-labels="false"
          :stats="periodicStat.status.data"
          @search="(parameters) => {
            applyFilter({
              _periodicStatSection: 'status',
              _periodicStatRange: parameters.range,
              _periodicStatGroup: parameters.group,
            })
          }"
          :apiRequestIsPerforming="!!getActiveRequestsCount"
          :export-types="exportTypes"
          @export="format => handleExport(periodicStat.status, format)"
        />
      </b-col>
    </b-row>


  </div>
</template>

<script>
import getNested from 'lodash-es/get'
import moment from 'moment'
import { mapGetters, mapActions } from 'vuex'
import { downloadAsFileByURL } from '@/shared/download-utils'

import FilterPanel from '@/components/widgets/FilterPanel'
import {
  applyFilterValues,
  getFormattedFilters,
  getAPIFilterValues,
  getCurrentFilterValues,
} from '@/components/widgets/FilterPanel'
import PeriodicStats from '@/components/tradeflows/PeriodicStats'


export default {
  name: 'fleet-growth',

  components: {
    FilterPanel,
    PeriodicStats,
  },

  data() {
    return {
      filterSchema: {
        segment: {
          name: 'segment',
          apiName: 'segment',
          type: 'multiselect',
          placeholder: 'segment',
          value: [],
          choices: [
            {text: 'Capesize', value: 'capesize'},
            {text: 'Handysize', value: 'handysize'},
            {text: 'Panamax', value: 'panamax'},
            {text: 'Supramax', value: 'supramax'},
          ],
        },

        metric: {
          name: 'metric',
          apiName: 'metric',
          type: 'select-buttons',
          label: 'Metric:',

          value: 'count',  // override default choice
          choices: [
            {text: 'Vessel count', value: 'count'},
            {text: 'Vessel DWT', value: 'dwt'},
          ],
        },
      },

      exportTypes: ['csv'],

      rangeChoices: [
        {text: 'Monthly', value: 'monthly'},
        {text: 'Yearly', value: 'year'},
        {text: 'Quarterly', value: 'quarter'},
        {text: 'Weekly', value: 'weekly'},
        {text: 'Daily', value: 'daily'}
      ],

      groupChoices: [
        {text: 'No grouping', value: null},
        {text: 'Segment', value: 'segment'},
        {text: 'Sub segment', value: 'sub_segment'},
      ],

      extendedFilterValues: {},

      periodicStat: {
        development: {
          name: 'development',
          data: [],
          group: null,
          range: 'monthly',
          method: 'getTonnageFleetDevelopment',
        },
        status: {
          name: 'status',
          data: [],
          group: null,
          range: 'monthly',
          method: 'getTonnageFleetStatus',
        },
      },

      apiRequestData: {},
    };
  },

  methods: {
    ...mapActions(
      'api/tonnage', [
        'getTonnageFleetDevelopment',
        'getTonnageFleetStatus',
      ]
    ),

    async applyFilter(filterValues) {
      // Possible variants here:
      // 1. filterValues from filter-panel
      // 2. extended filterValues from chart filters (range, group)

      let apiFilterValues = {
        ...getCurrentFilterValues(this.filterSchema),  // current state
        ...this.getBaseFilterParameters(filterValues),  // new base parameters, if available from filter-panel
      }

      applyFilterValues(this.filterSchema, apiFilterValues);

      let targetSectionNames = this.getTargetSectionNames(filterValues);
      for(let sectionName of targetSectionNames) {
        await this.loadSection(sectionName);
      }
    },

    getTargetSectionNames(filterValues) {
      let currentSectionName = filterValues._periodicStatSection;

      if (currentSectionName) {
        let currentSection = this.periodicStat[currentSectionName];
        currentSection.range = filterValues._periodicStatRange;
        currentSection.group = filterValues._periodicStatGroup;
        return [currentSectionName];
      } else {
        return Object.keys(this.periodicStat);
      }
    },

    async loadSection(sectionName) {
      const currentSection = this.periodicStat[sectionName];
      let parameters = this.getParametersForSection(currentSection);
      // console.log(`request parameters for "${sectionName}"`, JSON.stringify(parameters, null, 2));

      let response = await this[currentSection.method](parameters) || {};
      currentSection.data = getNested(response, 'data.data', []);  // .slice(1, 20)
      // console.log('response', JSON.stringify(currentSection.data.slice(0, 5), null, 2));

      this.$set(
        this.apiRequestData, sectionName, getNested(response, 'requestData', null)
      );
    },

    getParametersForSection(currentSection) {
      // filters must be applied with "applyFilterValues" to retrieve an actual version
      const currentParameters = getCurrentFilterValues(this.filterSchema);

      return {
        ...getAPIFilterValues(this.filterSchema, currentParameters),
        range: currentSection.range || undefined,
        group: currentSection.group || undefined,
      };
    },

    getBaseFilterParameters(filterValues) {
      let parameters = {};
      Object.keys(filterValues).forEach(fieldName => {
        if(fieldName.startsWith('_')) {
          return;
        }
        parameters[fieldName] = filterValues[fieldName];
      });
      return parameters;
    },

    async handleExport(section, format) {
      const filterParameters = {
        ...this.getParametersForSection(section),
        format,
      }
      let response = await this[section.method](filterParameters) || {};
      const timestamp = moment().format('YYYY-MM-DD-HH-mm');

      downloadAsFileByURL(
        getNested(response, 'data.csv', '--'),  // csv url
        `fleet_${section.name}_export_${timestamp}.${format}`
      );
    },
  },

  computed: {
    ...mapGetters('api', ['getActiveRequestsCount']),
    ...mapGetters('api/presets', ['getActivePreset']),

    computedFilterParameters() {
      return getFormattedFilters(this.filterSchema);
    },
  },

  watch: {
    getActivePreset: {
      immediate: true,
      handler: async function (preset) {
        if(preset && this.$route.path !== preset.viewName) {
          return;
        }

        // if(!this.filtersReady) {
        //   await this.loadFilters();
        // }

        if(preset) {
          const filterValues = getNested(preset, 'params.filterPanel', {});
          applyFilterValues(this.filterSchema, filterValues);
        }
      },
    },
  },
}
</script>

<style scoped>
  .page-container >>> .card-content {
    min-height: 460px;
  }
</style>

<style scoped lang="scss">

</style>
